import { Injectable, inject, isDevMode, signal } from '@angular/core';
import { GuardsCheckEnd, NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';

/** How often an open tab asks the server whether a newer build is deployed. */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** A tab coming back into view checks again only if its last check is older than this. */
const RECHECK_ON_FOCUS_MS = 60 * 1000;
/** How long "Más tarde" hides the notice before it comes back. */
const SNOOZE_MS = 30 * 60 * 1000;
/** A missing lazy file reloads a tab at most once in this window, so a broken deploy cannot loop. */
const CHUNK_RELOAD_WINDOW_MS = 60 * 1000;
const CHUNK_RELOAD_KEY = 'app-update:chunk-reload-at';

/** The content-hashed entry files a production build names in index.html. */
const ENTRY_FILE = /(?:^|\/)(?:main|polyfills|styles)-[A-Za-z0-9]+\.(?:js|css)$/;
/** What Chrome, Firefox and Safari say when a lazy-loaded file is gone from the server. */
const CHUNK_LOAD_ERROR = /dynamically imported module|Importing a module script failed/i;

/**
 * Gets tabs that stay open across a deploy onto the new build.
 *
 * index.html is served uncached, so opening or reloading the app always runs the
 * latest build; only a tab that is already open is left behind. A build names its
 * entry bundles by content hash (`main-<hash>.js`, `styles-<hash>.css`), so
 * comparing the names in the deployed index.html with the ones this document
 * loaded tells whether a reload would run different code, with no version file
 * to keep in step with the build.
 *
 * Once a newer build is detected, the next change of page is a full page load
 * instead of an in-app navigation (the user is leaving that page anyway), and
 * `UpdateNoticeComponent` offers a reload to whoever stays where they are.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly _router = inject(Router);

  /** A newer build is deployed than the one this tab runs. */
  readonly updateAvailable = signal(false);
  /** The user postponed the notice; it comes back after `SNOOZE_MS`. */
  readonly snoozed = signal(false);

  private _runningBuild = '';
  private _lastCheckAt = 0;
  private _checkTimer?: ReturnType<typeof setInterval>;

  /** Start watching for deploys. Called once, from the root component. */
  start(): void {
    // The dev server serves unhashed bundles and rebuilds in place.
    if (isDevMode()) return;
    this._runningBuild = buildFingerprint(document);
    if (!this._runningBuild) return;

    this._checkTimer = setInterval(() => void this.check(), CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - this._lastCheckAt >= RECHECK_ON_FOCUS_MS) void this.check();
    });

    // GuardsCheckEnd: the guards have let the navigation through, and the target
    // page's lazy file (which the deploy may have removed) is not requested yet.
    // Query-only changes (filters, tabs) stay in-app.
    this._router.events
      .pipe(filter((event): event is GuardsCheckEnd => event instanceof GuardsCheckEnd))
      .subscribe((event) => {
        if (!this.updateAvailable() || !event.shouldActivate) return;
        if (pathOf(event.urlAfterRedirects) === pathOf(this._router.url)) return;
        window.location.assign(event.urlAfterRedirects);
      });
  }

  /** Ask the server which build it serves now. Failures are ignored: the next check retries. */
  async check(): Promise<void> {
    if (!this._runningBuild || this.updateAvailable()) return;
    this._lastCheckAt = Date.now();
    try {
      const response = await fetch(new URL('index.html', document.baseURI), { cache: 'no-store' });
      if (!response.ok) return;
      const html = await response.text();
      const deployed = buildFingerprint(new DOMParser().parseFromString(html, 'text/html'));
      if (!deployed || deployed === this._runningBuild) return;
      this.updateAvailable.set(true);
      clearInterval(this._checkTimer);
    } catch {
      // Offline, or the container is being replaced mid-deploy.
    }
  }

  /** Reload now, onto the deployed build. */
  reload(): void {
    window.location.reload();
  }

  /** Hide the notice for a while; the full load on the next page change still applies. */
  snooze(): void {
    this.snoozed.set(true);
    setTimeout(() => this.snoozed.set(false), SNOOZE_MS);
  }

  /**
   * Router navigation-error hook: a page whose lazy file the last deploy removed
   * is loaded fresh from the server instead of failing.
   */
  handleNavigationError(error: NavigationError): void {
    if (isChunkLoadError(error.error) && claimChunkReload()) window.location.assign(error.url);
  }
}

/** The sorted names of the hashed entry files a document loads; equal for the same build. */
function buildFingerprint(doc: Document): string {
  const names = Array.from(doc.querySelectorAll('script[src], link[rel="stylesheet"][href]'))
    .map((el) => el.getAttribute('src') ?? el.getAttribute('href') ?? '')
    .filter((name) => ENTRY_FILE.test(name));
  return [...new Set(names)].sort().join(' ');
}

function pathOf(url: string): string {
  return url.split(/[?#]/)[0];
}

function isChunkLoadError(error: unknown): boolean {
  return CHUNK_LOAD_ERROR.test(error instanceof Error ? error.message : String(error));
}

/**
 * True at most once per window per tab. A deploy that is really missing a file
 * must not reload forever, and without storage there is no way to tell, so no reload.
 */
function claimChunkReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
    if (Date.now() - last < CHUNK_RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}
